import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  Users, 
  MagnifyingGlass, 
  Trophy,
  Target,
  Lightning,
  Ranking,
  CheckCircle,
  UserCirclePlus,
  CirclesThreePlus
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function Players() {
  const { user, login, refreshUser } = useAuth();
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [sortBy, setSortBy] = useState("ranking");
  const [loading, setLoading] = useState(true);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const fetchPlayers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (tierFilter !== "all") params.append("tier", tierFilter);
      params.append("sort_by", sortBy);
      
      const response = await axios.get(`${API}/players?${params.toString()}`);
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setLoading(false);
    }
  }, [tierFilter, sortBy]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  const handleClaimClick = (player) => {
    if (!user) {
      login();
      return;
    }
    setSelectedPlayer(player);
    setClaimDialogOpen(true);
  };

  const handleClaimConfirm = async () => {
    if (!selectedPlayer) return;
    
    setClaiming(true);
    try {
      const res = await axios.post(
        `${API}/players/${selectedPlayer.id}/claim`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message || "Claim request submitted! Waiting for admin approval.");
      fetchPlayers();
      setClaimDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit claim request");
    } finally {
      setClaiming(false);
    }
  };

  const filteredPlayers = players.filter(player =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    player.nickname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" weight="fill" />
            Annadil Burhani DSM Members
          </h1>
          <p className="text-muted-foreground mt-1">
            {players.length} registered members
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/players/head-to-head">
            <Button variant="outline" className="border-border" data-testid="h2h-btn">
              <CirclesThreePlus className="w-4 h-4 mr-2" />Head to Head
            </Button>
          </Link>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-card border-border w-full sm:w-48"
              data-testid="search-players"
            />
          </div>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger className="w-full sm:w-36 bg-card border-border" data-testid="filter-tier">
              <SelectValue placeholder="Filter by tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="premier">Premier</SelectItem>
              <SelectItem value="challenger">Challenger</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-36 bg-card border-border" data-testid="sort-by">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ranking">Ranking</SelectItem>
              <SelectItem value="total_points">Points</SelectItem>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Claim Profile Info */}
      {user && !user.claimed_player_id && (
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserCirclePlus className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium">Claim Your Profile</p>
                <p className="text-sm text-muted-foreground">
                  Find your name below and click "Claim" to link it to your account
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Players Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        {filteredPlayers.map((player) => {
          const winRate = player.matches_played > 0 
            ? ((player.matches_won / player.matches_played) * 100).toFixed(0) 
            : 0;
          const isClaimed = !!player.claimed_by_email;
          const isMyProfile = user?.claimed_player_id === player.id;

          return (
            <motion.div key={player.id} variants={cardVariants}>
              <Card className="player-card h-full relative">
                <CardContent className="p-0">
                  {/* Claimed Badge */}
                  {isClaimed && (
                    <div className="absolute top-2 left-2 z-10">
                      <Badge className="bg-primary/20 text-primary border-primary/30">
                        <CheckCircle className="w-3 h-3 mr-1" weight="fill" />
                        Claimed
                      </Badge>
                    </div>
                  )}

                  {/* Header with rank */}
                  <div className="relative p-4 pb-12">
                    <div className="absolute top-3 right-3">
                      <Badge className={player.tier === 'premier' ? 'tier-premier' : 'tier-challenger'}>
                        {player.tier === 'premier' ? 'Premier' : 'Challenger'}
                      </Badge>
                    </div>
                    <div className={`absolute ${isClaimed ? 'top-10' : 'top-3'} left-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      player.ranking === 1 ? 'rank-1' : 
                      player.ranking === 2 ? 'rank-2' : 
                      player.ranking === 3 ? 'rank-3' : 
                      'bg-muted text-muted-foreground'
                    }`}>
                      {player.ranking}
                    </div>
                  </div>

                  {/* Avatar */}
                  <div className="flex justify-center -mt-10 mb-4">
                    <Avatar className="h-20 w-20 border-4 border-card shadow-lg">
                      <AvatarImage src={player.photo_url} alt={player.name} />
                      <AvatarFallback className="bg-muted text-muted-foreground text-xl">
                        {getInitials(player.name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>

                  {/* Player Info */}
                  <div className="px-4 pb-4 text-center">
                    <h3 className="font-heading font-bold text-lg text-foreground">{player.name}</h3>
                    {player.nickname && (
                      <p className="text-sm text-muted-foreground">"{player.nickname}"</p>
                    )}
                    {player.age && (
                      <p className="text-xs text-muted-foreground mt-1">Age: {player.age}</p>
                    )}

                    {/* Points */}
                    <div className="mt-4 p-3 rounded-lg bg-muted/30">
                      <p className="text-2xl font-heading font-bold text-primary">{player.total_points}</p>
                      <p className="text-xs text-muted-foreground">Total Points</p>
                    </div>

                    {/* Stats Grid */}
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="p-2 rounded bg-muted/20">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <Target className="w-3 h-3" />
                          <span className="text-xs">Win Rate</span>
                        </div>
                        <p className="font-bold text-sm">{winRate}%</p>
                      </div>
                      <div className="p-2 rounded bg-muted/20">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <Trophy className="w-3 h-3" />
                          <span className="text-xs">Titles</span>
                        </div>
                        <p className="font-bold text-sm">{player.tournament_wins}</p>
                      </div>
                      <div className="p-2 rounded bg-muted/20">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <Lightning className="w-3 h-3" />
                          <span className="text-xs">Centuries</span>
                        </div>
                        <p className="font-bold text-sm">{player.century_breaks}</p>
                      </div>
                      <div className="p-2 rounded bg-muted/20">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <Ranking className="w-3 h-3" />
                          <span className="text-xs">High Break</span>
                        </div>
                        <p className="font-bold text-sm text-secondary">{player.highest_break}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex gap-2">
                      <Link to={`/players/${player.id}`} className="flex-1">
                        <Button variant="outline" className="w-full border-border" size="sm">
                          View Profile
                        </Button>
                      </Link>
                      {!isClaimed && user && !user.claimed_player_id && (
                        <Button 
                          onClick={() => handleClaimClick(player)}
                          className="bg-primary hover:bg-primary/90"
                          size="sm"
                          data-testid={`claim-${player.id}`}
                        >
                          Claim
                        </Button>
                      )}
                    </div>

                    {isMyProfile && (
                      <p className="mt-2 text-xs text-primary font-medium">This is your profile</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {filteredPlayers.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No players found</p>
        </div>
      )}

      {/* Claim Dialog */}
      <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Request Profile Claim</DialogTitle>
            <DialogDescription>
              You are requesting to claim <strong>{selectedPlayer?.name}</strong> as your profile.
              This will link your Google account ({user?.email}) to this player.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Your request will be reviewed by an admin. You'll be notified once it's approved.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleClaimConfirm} disabled={claiming} data-testid="confirm-claim-btn">
              {claiming ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
